import { analysisClient } from "@/shared/lib/supabase";
import { getKeyOnlyQueryConfig } from "@/shared/lib/key-only-query-config";
import { queryOptions } from "@tanstack/react-query";
import { fetchTenant, fetchTenantVariableConfig } from "./tenant-variables.api";
import { getGlobalTenantId, getUserTenantId } from "./tenantId";
import type { TenantData } from "./types";

export async function fetchTenantData(userId: string): Promise<TenantData> {
  const { data, error } = await analysisClient
    .from("tenant_user")
    .select(
      "tenant:tenant!tenant_user_tenant_id_fkey(*, setting:tenant_setting!tenant_setting_tenant_id_fkey(*), collections:tenant_collection!tenant_collection_tenant_id_fkey(*))",
    )
    .eq("auth_user_id", userId)
    .single();

  if (error) throw error;
  if (!data?.tenant) throw new Error("Tenant context not found");

  const { setting, collections, ...tenant } = data.tenant;
  return {
    tenant,
    setting: setting ?? null,
    collections: collections ?? [],
  } as TenantData;
}

export const tenantKeys = {
  all: getKeyOnlyQueryConfig(["tenants"]),
  current: () => getKeyOnlyQueryConfig(["tenants", "current"]),
  global: () => getKeyOnlyQueryConfig(["tenants", "global"]),
  tenant: () => getKeyOnlyQueryConfig(["tenants", "tenant"]),
  variables: () => getKeyOnlyQueryConfig(["tenants", "variables"]),
};

export const tenantQueries = {
  all: tenantKeys.all,
  current: (userId?: string) =>
    queryOptions({
      queryKey: [...tenantKeys.current().queryKey, userId],
      queryFn: () => fetchTenantData(userId as string),
      enabled: Boolean(userId),
      staleTime: Infinity,
      gcTime: Infinity,
    }),
  global: () =>
    queryOptions({
      queryKey: tenantKeys.global().queryKey,
      queryFn: getGlobalTenantId,
    }),
  tenant: (tenantId: string) =>
    queryOptions({
      queryKey: [...tenantKeys.tenant().queryKey, tenantId],
      queryFn: () => fetchTenant(tenantId),
      enabled: Boolean(tenantId),
    }),
  variables: (tenantId: string) =>
    queryOptions({
      queryKey: [...tenantKeys.variables().queryKey, tenantId],
      queryFn: () => fetchTenantVariableConfig(tenantId),
      enabled: Boolean(tenantId),
    }),
};

export { getGlobalTenantId, getUserTenantId };
